// @ts-strict-ignore
import {ParamComponent} from 'panel';
import moment from 'moment';

import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {dateStringComparator} from 'common/util';
import {Cache} from 'common/util/cache';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {Annotation, HighchartsAnnotationButtonLocationDetails} from 'common/charts/visualization/line-chart/types';

import {template} from './template';
import css from './index.inline.styl';

export enum AnnotationViewType {
  DEFAULT = `default`,
  PREVIEW = `preview`,
}

export type ViewAnnotationsListParams = {
  activeAnnotation?: HighchartsAnnotationButtonLocationDetails;
  activeAnnotationGroup?: Annotation[];
  annotationDescriptionDraft?: string;
  canWriteAnnotations?: boolean;
  editingAnnotation?: Annotation;
  deletingAnnotation?: Annotation;
  type?: AnnotationViewType;
  timeUnit: TimeUnit;
};

export type ViewAnnotationsListJsxProps = {
  params: ViewAnnotationsListParams;
  on: {
    closeAnnotationPopup: () => void;
    showCreateAnnotation: (ev: CustomEvent<HighchartsAnnotationButtonLocationDetails>) => void;
    editAnnotation: (ev: CustomEvent<{editingAnnotation: Annotation; annotationDescriptionDraft: string}>) => void;
    deleteAnnotation: (ev: CustomEvent<Annotation>) => void;
    updateAnnotationDescriptionDraft: (ev: CustomEvent<string>) => void;
    saveAnnotation: () => void;
    editCanceled: () => void;
    deleteConfirmed: () => void;
    deleteCanceled: () => void;
  };
};

export class ViewAnnotationsList extends ParamComponent<ViewAnnotationsListParams> {
  static parseDateCache: Cache = new Cache();

  get config() {
    return {
      css,
      useShadowDom: true,
      template,
      params: {
        activeAnnotation: Object,
        activeAnnotationGroup: Array,
        annotationDescriptionDraft: String,
        canWriteAnnotations: Boolean,
        editingAnnotation: Object,
        deletingAnnotation: Object,

        type: {
          default: AnnotationViewType.DEFAULT,
          type: String,
        },
        timeUnit: String,
      },
      helpers: {
        AnnotationViewType,
        closeAnnotationListPopup: () => {
          this.dispatchEvent(new CustomEvent(`closeAnnotationPopup`));
        },
        createAnnotation: () => {
          this.dispatchEvent(new CustomEvent(`showCreateAnnotation`, {detail: this.params.activeAnnotation}));
        },
        getActiveAnnotationGroup: () => {
          const isPreview = this.params.type === AnnotationViewType.PREVIEW;
          let activeAnnotationGroup = this.params.activeAnnotationGroup || [];
          activeAnnotationGroup = activeAnnotationGroup.sort(
            dateStringComparator({
              transform: (a) => a.date,
              parseDateCache: ViewAnnotationsList.parseDateCache,
              parseDateConfig: {iso: true},
            }),
          );
          const numAnnotations = activeAnnotationGroup.length;

          // Preview only shows 2, then has a + X more... below
          activeAnnotationGroup = isPreview ? activeAnnotationGroup.slice(0, 2) : activeAnnotationGroup;

          return {activeAnnotationGroup, numAnnotations};
        },
        formatDate: (isoDate) => {
          switch (this.params.timeUnit) {
            case TimeUnit.Day:
            case TimeUnit.Hour:
            case TimeUnit.Minute:
              return moment(isoDate).format(`ha`);
            default:
              return moment(isoDate).format(`ha, MMM Do`);
          }
        },

        edit: (annotation): void => {
          this.dispatchEvent(
            new CustomEvent(`editAnnotation`, {
              detail: {editingAnnotation: annotation, annotationDescriptionDraft: annotation.description},
            }),
          );
        },

        delete: (annotation): void => {
          this.dispatchEvent(new CustomEvent(`deleteAnnotation`, {detail: annotation}));
        },

        handleDescriptionChanged: (ev): void => {
          this.dispatchEvent(new CustomEvent(`updateAnnotationDescriptionDraft`, {detail: ev.target.value}));
        },

        save: () => {
          this.dispatchEvent(new CustomEvent(`saveAnnotation`));
        },

        editCanceled: () => {
          this.dispatchEvent(new CustomEvent(`editCanceled`));
        },

        deleteConfirmed: () => {
          this.dispatchEvent(new CustomEvent(`deleteConfirmed`));
        },

        deleteCanceled: () => {
          this.dispatchEvent(new CustomEvent(`deleteCanceled`));
        },
      },
    };
  }
}

export const ViewAnnotationsListEl = defineAndMakeJsxFactory<ViewAnnotationsListJsxProps>(
  `view-annotations-list`,
  ViewAnnotationsList,
);
export const ViewAnnotationsListReact = panel2react<ViewAnnotationsListJsxProps>(
  `view-annotations-list`,
  ViewAnnotationsList,
);
